import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";

type TooltipArrowDirection = "up" | "right" | "down" | "left";
type TooltipTextAlign = "left" | "center" | "right";

interface TooltipProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "content"
> {
  text?: string;
  content?: React.ReactNode;
  iconName?: string;
  arrow?: TooltipArrowDirection;
  textAlign?: TooltipTextAlign;
  backgroundColor?: string;
  textColor?: string;
  minWidth?: number;
  maxWidth?: number;
}

interface TooltipHoverProps extends TooltipProps {
  children: React.ReactNode;
  offset?: number;
  fullWidth?: boolean;
}

const TOOLTIP_PADDING_VERTICAL = 16;
const TOOLTIP_PADDING_HORIZONTAL = 12;
const TOOLTIP_BORDER_RADIUS = 16;
const ARROW_SIZE = 18;
const DEFAULT_OFFSET = 12;

const getTooltipContainerProps = (
  props: TooltipProps,
): React.HTMLAttributes<HTMLDivElement> => {
  const {
    text: _text,
    content: _content,
    iconName: _iconName,
    arrow: _arrow,
    textAlign: _textAlign,
    backgroundColor: _backgroundColor,
    textColor: _textColor,
    minWidth: _minWidth,
    maxWidth: _maxWidth,
    ...rest
  } = props;

  return rest;
};

const getTooltipPropsFromHover = (props: TooltipHoverProps): TooltipProps => {
  const { children: _children, offset: _offset, fullWidth: _fullWidth, ...rest } = props;
  return rest;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

// Conservative upper bound: maxWidth(220) + 2×padding(24) + arrow overhang(9)
const SAFE_BUBBLE_WIDTH = 253;
const SAFE_BUBBLE_HALF = SAFE_BUBBLE_WIDTH / 2;

const getPortalStyle = (
  rect: DOMRect,
  placement: TooltipArrowDirection,
  offset: number,
): React.CSSProperties => {
  switch (placement) {
    case "down":
      return {
        bottom: window.innerHeight - rect.top + offset,
        left: clamp(
          rect.left + rect.width / 2,
          SAFE_BUBBLE_HALF,
          window.innerWidth - SAFE_BUBBLE_HALF,
        ),
      };
    case "up":
      return {
        top: clamp(rect.bottom + offset, 0, window.innerHeight),
        left: clamp(
          rect.left + rect.width / 2,
          SAFE_BUBBLE_HALF,
          window.innerWidth - SAFE_BUBBLE_HALF,
        ),
      };
    case "left":
      return {
        left: clamp(
          rect.right + offset,
          0,
          window.innerWidth - SAFE_BUBBLE_WIDTH,
        ),
        top: clamp(rect.top + rect.height / 2, 0, window.innerHeight),
      };
    case "right":
      return {
        right: clamp(
          window.innerWidth - rect.left + offset,
          SAFE_BUBBLE_WIDTH,
          window.innerWidth,
        ),
        top: clamp(rect.top + rect.height / 2, 0, window.innerHeight),
      };
  }
};

/**
 * Tooltip bubble that renders optional icon, text, and an arrow.
 */
export const Tooltip = (props: TooltipProps): React.ReactElement => {
  const {
    text,
    content,
    iconName,
    arrow: arrowProp,
    textAlign: textAlignProp,
    backgroundColor: backgroundColorProp,
    textColor: textColorProp,
    minWidth: minWidthProp,
    maxWidth: maxWidthProp,
  } = props;
  const hasIcon = Boolean(iconName);
  const resolvedContent = content ?? text ?? "";
  const arrow = arrowProp ?? "down";
  const textAlign = textAlignProp ?? (hasIcon ? "left" : "center");
  const backgroundColor = backgroundColorProp ?? ColorV2.Blue6;
  const textColor = textColorProp ?? ColorV2.Blue3;
  const minWidth = minWidthProp ?? 140;
  const maxWidth = maxWidthProp ?? 220;
  const containerProps = getTooltipContainerProps(props);

  return (
    <Container
      $hasIcon={hasIcon}
      $backgroundColor={backgroundColor}
      $textColor={textColor}
      {...containerProps}
    >
      {iconName && (
        <IconWrapper>
          <CustomIcon name={iconName} size="24px" color={ColorV2.Blue3} />
        </IconWrapper>
      )}
      <Content
        $textAlign={textAlign}
        $minWidth={minWidth}
        $maxWidth={maxWidth}
      >
        {typeof resolvedContent === "string" ? (
          <Text>{resolvedContent}</Text>
        ) : (
          resolvedContent
        )}
      </Content>
      <Arrow $direction={arrow} $backgroundColor={backgroundColor} />
    </Container>
  );
};

/**
 * Wraps a trigger and shows the tooltip bubble on hover via a fixed-position portal,
 * so it is never clipped by overflow-hidden/scroll ancestors.
 */
export const TooltipHover = (props: TooltipHoverProps): React.ReactElement => {
  const {
    children,
    arrow: arrowProp,
    offset: offsetProp,
    backgroundColor,
    textColor,
    fullWidth,
  } = props;
  const arrow = arrowProp ?? "down";
  const offset = offsetProp ?? DEFAULT_OFFSET;
  const tooltipProps = getTooltipPropsFromHover(props);

  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const [isPressedOpen, setIsPressedOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  const isOpen = isHoverOpen || isPressedOpen;

  const updateRect = useCallback(() => {
    if (triggerRef.current) {
      setRect(triggerRef.current.getBoundingClientRect());
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [isOpen, updateRect]);

  useEffect(() => {
    if (!isPressedOpen) return;
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        setIsPressedOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isPressedOpen]);

  return (
    <HoverWrap
      ref={wrapperRef}
      onMouseEnter={() => {
        updateRect();
        setIsHoverOpen(true);
      }}
      onMouseLeave={() => {
        setIsHoverOpen(false);
        setIsPressedOpen(false);
      }}
      $fullWidth={Boolean(fullWidth)}
    >
      <Trigger
        ref={triggerRef}
        onClick={() => {
          updateRect();
          setIsPressedOpen((prev) => !prev);
          setIsHoverOpen(true);
        }}
        $fullWidth={Boolean(fullWidth)}
      >
        {children}
      </Trigger>
      {createPortal(
        <PortalBubble
          $placement={arrow}
          $isOpen={isOpen && rect !== null}
          style={rect ? getPortalStyle(rect, arrow, offset) : undefined}
        >
          <Tooltip
            {...tooltipProps}
            arrow={arrow}
            backgroundColor={backgroundColor}
            textColor={textColor}
          />
        </PortalBubble>,
        document.body,
      )}
    </HoverWrap>
  );
};

const Container = styled.div<{
  $hasIcon: boolean;
  $backgroundColor: string;
  $textColor: string;
}>`
  ${TypographyV2.s1}
  position: relative;
  width: fit-content;
  padding: ${TOOLTIP_PADDING_VERTICAL}px ${TOOLTIP_PADDING_HORIZONTAL}px;
  border-radius: ${TOOLTIP_BORDER_RADIUS}px;
  background-color: ${(props) => props.$backgroundColor};
  color: ${(props) => props.$textColor};
  display: flex;
  gap: 16px;
  align-items: center;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`;

const Content = styled.div<{
  $textAlign: TooltipTextAlign;
  $minWidth: number;
  $maxWidth: number;
}>`
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 auto;
  min-width: ${(props) => props.$minWidth}px;
  max-width: ${(props) => props.$maxWidth}px;
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;
  text-align: ${(props) => props.$textAlign};
  align-items: ${(props) => {
    if (props.$textAlign === "right") return "flex-end";
    if (props.$textAlign === "center") return "center";
    return "flex-start";
  }};
`;

const Text = styled.span`
  ${TypographyV2.s1}
  color: inherit;
`;

const Arrow = styled.span<{
  $direction: TooltipArrowDirection;
  $backgroundColor: string;
}>`
  position: absolute;
  width: ${ARROW_SIZE}px;
  height: ${ARROW_SIZE}px;
  background-color: ${(props) => props.$backgroundColor};
  transform: rotate(45deg);

  ${(props) =>
    props.$direction === "up" &&
    css`
      top: -${ARROW_SIZE / 2}px;
      left: 50%;
      transform: translateX(-50%) rotate(45deg);
    `}

  ${(props) =>
    props.$direction === "down" &&
    css`
      bottom: -${ARROW_SIZE / 2}px;
      left: 50%;
      transform: translateX(-50%) rotate(45deg);
    `}

  ${(props) =>
    props.$direction === "left" &&
    css`
      left: -${ARROW_SIZE / 2}px;
      top: 50%;
      transform: translateY(-50%) rotate(45deg);
    `}

  ${(props) =>
    props.$direction === "right" &&
    css`
      right: -${ARROW_SIZE / 2}px;
      top: 50%;
      transform: translateY(-50%) rotate(45deg);
    `}
`;

const PortalBubble = styled.div<{
  $placement: TooltipArrowDirection;
  $isOpen: boolean;
}>`
  position: fixed;
  z-index: 9999;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.$isOpen ? "auto" : "none")};
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;

  ${(props) =>
    props.$placement === "down" &&
    css`
      transform: translateX(-50%) translateY(${props.$isOpen ? 0 : 4}px);
    `}
  ${(props) =>
    props.$placement === "up" &&
    css`
      transform: translateX(-50%) translateY(${props.$isOpen ? 0 : -4}px);
    `}
  ${(props) =>
    props.$placement === "left" &&
    css`
      transform: translateY(-50%) translateX(${props.$isOpen ? 0 : 4}px);
    `}
  ${(props) =>
    props.$placement === "right" &&
    css`
      transform: translateY(-50%) translateX(${props.$isOpen ? 0 : -4}px);
    `}
`;

const HoverWrap = styled.div<{ $fullWidth: boolean }>`
  display: ${(props) => (props.$fullWidth ? "flex" : "inline-flex")};
  align-items: center;
  align-self: flex-start;
  width: ${(props) => (props.$fullWidth ? "100%" : "fit-content")};
  max-width: 100%;
`;

const Trigger = styled.span<{ $fullWidth: boolean }>`
  position: relative;
  display: ${(props) => (props.$fullWidth ? "flex" : "inline-flex")};
  align-items: center;
  width: ${(props) => (props.$fullWidth ? "100%" : "auto")};
`;
